import { ref, onUnmounted } from 'vue';

export function useLipSync() {
  const audioContext = ref<AudioContext | null>(null);
  const analyser = ref<AnalyserNode | null>(null);
  const silentGain = ref<GainNode | null>(null);
  const dataArray = ref<Uint8Array | null>(null);
  const audioConnected = ref(false);
  const lipSyncRetryTimer = ref<number | null>(null);

  const ensureAudioGraph = (): void => {
    if (!audioContext.value) {
      audioContext.value = new (
        window.AudioContext || (window as any).webkitAudioContext
      )();
      analyser.value = audioContext.value.createAnalyser();
      analyser.value.fftSize = 32;
      dataArray.value = new Uint8Array(analyser.value.frequencyBinCount);

      // 分析链路必须接到 destination 才会跑图，但增益为 0，避免第二路可听声音
      silentGain.value = audioContext.value.createGain();
      silentGain.value.gain.value = 0;
      analyser.value.connect(silentGain.value);
      silentGain.value.connect(audioContext.value.destination);
    }
  };

  const connectStreamToAnalyser = (srcObject: MediaStream): boolean => {
    if (!audioContext.value || !analyser.value || audioConnected.value) {
      return false;
    }

    if (!srcObject.getAudioTracks?.().length) {
      return false;
    }

    try {
      const streamSource = audioContext.value.createMediaStreamSource(srcObject);
      streamSource.connect(analyser.value);
      audioConnected.value = true;
      return true;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to connect stream to lip sync analyser:', error);
      }
      return false;
    }
  };

  const startLipSync = () => {
    if (audioConnected.value) {
      try {
        audioContext.value?.resume?.();
      } catch (e) {
        console.error('Failed to resume audio context:', e);
      }
      return;
    }

    ensureAudioGraph();

    try {
      audioContext.value?.resume?.();
    } catch (e) {
      console.error('Failed to resume audio context:', e);
    }

    const mediaElements = Array.from(
      document.querySelectorAll('audio, video')
    ) as HTMLAudioElement[];

    for (const el of mediaElements) {
      if (el.id === 'local-player') continue;

      const srcObject = el.srcObject as MediaStream | null;
      if (srcObject && connectStreamToAnalyser(srcObject)) {
        return;
      }
    }

    if (!lipSyncRetryTimer.value) {
      lipSyncRetryTimer.value = window.setInterval(() => {
        if (audioConnected.value) {
          if (lipSyncRetryTimer.value) {
            clearInterval(lipSyncRetryTimer.value);
            lipSyncRetryTimer.value = null;
          }
          return;
        }

        try {
          const elements = Array.from(
            document.querySelectorAll('audio, video')
          ) as HTMLAudioElement[];

          for (const el of elements) {
            if (el.id === 'local-player') continue;

            const srcObject = el.srcObject as MediaStream | null;
            if (srcObject && connectStreamToAnalyser(srcObject)) {
              if (lipSyncRetryTimer.value) {
                clearInterval(lipSyncRetryTimer.value);
                lipSyncRetryTimer.value = null;
              }
              break;
            }
          }
        } catch (e) {
          console.error('Lip sync retry error:', e);
        }
      }, 1000);
    }
  };

  const stopLipSync = () => {
    if (lipSyncRetryTimer.value) {
      clearInterval(lipSyncRetryTimer.value);
      lipSyncRetryTimer.value = null;
    }
  };

  const cleanup = () => {
    stopLipSync();
    audioConnected.value = false;
    silentGain.value = null;
    analyser.value = null;
    dataArray.value = null;
    if (audioContext.value) {
      audioContext.value.close();
      audioContext.value = null;
    }
  };

  onUnmounted(cleanup);

  return {
    audioContext,
    analyser,
    dataArray,
    audioConnected,
    startLipSync,
    stopLipSync,
    cleanup,
  };
};
